import React, { useEffect, useState } from "react";
import axios from "axios";
import Header from "./Components/Header";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import { BrowserRouter as Router, Route, Routes } from "react-router-dom";
import Contact from "./Pages/Contact";
import Home from "./Pages/Home";
import Pricing from "./Pages/Pricing";
import Footer from "./Components/Footer";
import PlayListExport from "./Pages/PlaylistExport";
import YouTubeTagsRankChecker from "./Pages/YtTagsRankChecker";
import YouTubeSearchResultsExport from "./Pages/YtSearchExport";
import YouTubeTagGenerator from "./Pages/YtTagGenerator";
import YouTubeTitleGenerator from "./Pages/YtTitleGenerator";
import GoogleSearchResultsExport from "./Pages/GoogleSearchResultsExport";
import YouTubeThumbNailDownload from "./Pages/YtThumbnailDownload";
import YouTubeChannelBackup from "./Pages/YtChannelBackUp";
import YouTubeVideoRankChecker from "./Pages/YtVideoRankCheckers";
import YouTubeKeywordSuggestion from "./Pages/YtKeywordSuggestion";
import AmazonProductScraper from "./Pages/amazon-product-scraper";
import UserUsageDashboard from "./Pages/Dashboard";
import BulkEmailValidator from "./Pages/BulkEmailValidator";
import CircularProgress from "@mui/material/CircularProgress";
import ProfilePage from "./Pages/Profile";
import PrivacyPolicy from "./Pages/privacyPolicy";
import TermsOfUse from "./Pages/Terms";
import NotFoundPage from "./Pages/NotFound";
import Box from "@mui/material/Box";
import SubscriptionManagement from "./Pages/Subscription";
import SignUpIn from "./Pages/SignUpIn";
import VerifyEmailPage from "./Pages/VerifyEmail";
import ForgotPassword from "./Pages/ForgotPassword";
import ResetPassword from "./Pages/ResetPassword";
import RandomEmailGenerator from "./Pages/RandomEmailGenerator";
import JsonToCsvConverter from "./Pages/JsonToCSV";
import CsvToJsonConverter from "./Pages/CsvToJson";
import YouTubeTranscriptDownloader from "./Pages/YouTubeTranscriptDownload";
import YouTubeTranscriptWithTimestamps from "./Pages/YouTubeTimestampGenerator";
import YouTubePlaylistExportBlog from "./Blog/YouTubePlaylistExportBlog";
import YouTubeTagsRankCheckerBlog from "./Blog/YouTubeTagsRankCheckerBlog";
import RandomIPGenerator from "./Pages/RandomIpAddress";
import XmlToJsonConverter from "./Pages/XmlToJson";
import YamlToJsonConverter from "./Pages/YamlToJsonConverter";
import JsonToYamlConverter from "./Pages/JsonToYml";
import EmailValidator from "./Pages/email-validation-checker";
import YouTubeSearchExportBlog from "./Blog/YouTubeSearchResultsExportBlog";
import YouTubeTagGeneratorBlog from "./Blog/YouTubeTagGeneratorBlog";
import YouTubeTitleGeneratorBlog from "./Blog/YouTubeVideoTitleGeneratorBlog";
import EmailAliasGenerator from "./Pages/EmailAliasGenerator";
import { Navigate } from "react-router-dom";
import GoogleKeywordSuggestion from "./Pages/GoogleKeywordSuggestion";
import UrlEncoderTool from "./Pages/UrlEncoder";
import UrlDecoderTool from "./Pages/UrlDecoder";
import JsonFormatter from "./Pages/JsonFormatter";
import SSLCertificateChecker from "./Pages/SslCertificateChecker";
import YouTubeTagExtractor from "./Pages/YouTubeTagsExtractor";
import AdminPendingComments from "./Pages/AdminPendingComments";
import Clarity from "@microsoft/clarity";
import InspectletLoader from "./Components/InspectLet";
import DisposableEmailChecker from "./Pages/DisposableEmailChecker";

const theme = createTheme();

function App() {
  const [user, setUser] = useState(null);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    if (process.env.REACT_APP_ENV === "production") {
      Clarity.init("rl4gxqdwia");
    }
  }, []);
  useEffect(() => {
    const initializeAuth = async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const tokenFromURL = urlParams.get("token");
      let token;
      if (tokenFromURL) {
        localStorage.setItem("token", tokenFromURL);
        token = tokenFromURL;
        const preAuthState = JSON.parse(localStorage.getItem("preAuthState"));
        if (
          preAuthState &&
          preAuthState.path &&
          preAuthState.path !== "undefined"
        ) {
          window.history.replaceState(
            {},
            "",
            `${preAuthState.path}${preAuthState.search || ""}`
          );
          window.location.reload(); // ⬅️ Important: reload so React router re-evaluates
          localStorage.removeItem("preAuthState");
        }
      } else {
        token = localStorage.getItem("token");
      }
      if (token) {
        try {
          axios.defaults.headers.common["Authorization"] = `Bearer ${token}`;
          const res = await axios.get(
            `${process.env.REACT_APP_BACKEND_URL}/auth/user`
          );
          setUser(res.data);
          setIsLoggedIn(true);
        } catch (error) {
          console.error("Auth failed:", error);
          localStorage.removeItem("token");
          setUser(null);
          setIsLoggedIn(false);
        }
      } else {
        // ✅ Add this block
        setUser(null);
        setIsLoggedIn(false);
      }

      setLoading(false);
    };

    initializeAuth();
  }, []);

  const login = async () => {
    try {
      const primaryInputText =
        document.querySelector("[name='primary-textbox']")?.value || "";
      const secondaryInputText =
        document.querySelector("[name='secondary-textbox']")?.value || "";
      const primaryDropDown =
        document.querySelector("[name='primary-drop-down']")?.value || "";

      localStorage.setItem("primaryInputText", primaryInputText);
      localStorage.setItem("secondaryInputText", secondaryInputText);
      localStorage.setItem("primary-drop-down", primaryDropDown);

      // Save intended redirect path (to return user back after signup/login)
      localStorage.setItem(
        "preAuthState",
        JSON.stringify({
          path: window.location.pathname || "/",
          search: window.location.search || "",
        })
      );

      // Detect tool path dynamically (e.g., "/youtube/playlistExport" => "youtube_playlistExport")
      const currentPath = window.location.pathname.replace(/^\/+|\/+$/g, "");
      const toolKey = currentPath.replace(/[\/]/g, "_") || "unknown_tool";

      const guestUsedKey = `${toolKey}_guestUsed`;
      const guestIPKey = `${toolKey}_guestIP`;

      // If guest access already used, redirect immediately
      if (localStorage.getItem(guestUsedKey)) {
        window.location.href = `${process.env.REACT_APP_FRONTEND_URL}/welcome/signup`;
        return;
      }

      // Otherwise, fetch guest IP once and store it
      try {
        const res = await fetch("https://api64.ipify.org?format=json");
        const data = await res.json();
        if (data?.ip) {
          localStorage.setItem(guestIPKey, data.ip);
        }
      } catch (err) {
        console.warn("Failed to fetch guest IP address.", err);
      }

      // Finally redirect to signup/login
      window.location.href = `${process.env.REACT_APP_FRONTEND_URL}/welcome/signup`;
    } catch (e) {
      console.log("Login error:", e);
    }
  };

  const logout = async () => {
    try {
      await axios.get(`${process.env.REACT_APP_BACKEND_URL}/auth/logout`);
      localStorage.removeItem("token");
      setUser(null);
      setIsLoggedIn(false);
      window.location.href = "/"; // 👈 Full-page reload to home
    } catch (error) {
      console.error("Logout failed:", error);
    }
  };

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      {process.env.REACT_APP_ENV === "production" && <InspectletLoader />}
      <Router>
        <Header
          isLoggedIn={isLoggedIn}
          user={user}
          onLogout={logout}
          login={login}
          loading={loading}
        />
        <Routes>
          <Route path="/" element={<Home user={user} login={login} />} />
          <Route path="/contact" element={<Contact />} />
          <Route
            path="/pricing"
            element={
              <Pricing
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
                region={user?.country === "IN" ? "india" : "us"}
              />
            }
          />
          <Route
            path="/youtube/playlistExport"
            element={
              <PlayListExport
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/youtube/tags-rank-checker"
            element={
              <YouTubeTagsRankChecker
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/youtube/searchResultsExport"
            element={
              <YouTubeSearchResultsExport
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/youtube/tagGenerator"
            element={
              <YouTubeTagGenerator
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/youtube/video-title-generator"
            element={
              <YouTubeTitleGenerator
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/google/search-results-export"
            element={
              <GoogleSearchResultsExport
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/youtube/thumbnailDownloader"
            element={
              <YouTubeThumbNailDownload
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/youtube/channelBackUp"
            element={
              <YouTubeChannelBackup
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/youtube/video-rank-checker"
            element={
              <YouTubeVideoRankChecker
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/youtube/keyword-auto-suggestion"
            element={
              <YouTubeKeywordSuggestion
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/google-keyword-auto-suggestion"
            element={
              <GoogleKeywordSuggestion
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/misc/amazon-product-scraper"
            element={
              <AmazonProductScraper
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/misc/bulk-email-validator-tool"
            element={
              <BulkEmailValidator
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/dashboard"
            element={
              <UserUsageDashboard
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/profile"
            element={
              <ProfilePage user={user} login={login} isLoggedIn={isLoggedIn} />
            }
          />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-of-use" element={<TermsOfUse />} />
          <Route
            path="/subscription"
            element={
              <SubscriptionManagement
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route path="/welcome/signup" element={<SignUpIn />} />
          <Route path="/welcome/login" element={<SignUpIn />} />
          <Route path="/verify-email" element={<VerifyEmailPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route
            path="/random-email-generator"
            element={
              <RandomEmailGenerator
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/json-to-csv"
            element={
              <JsonToCsvConverter
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/csv-to-json"
            element={
              <CsvToJsonConverter
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/youtube-transcript-downloader"
            element={
              <YouTubeTranscriptDownloader
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/youtube-timestamper-generator"
            element={
              <YouTubeTranscriptWithTimestamps
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/blog/how-to-export-youtube-playlist-to-csv"
            element={<YouTubePlaylistExportBlog />}
          />
          <Route
            path="/blog/how-to-check-youtube-tags-ranking"
            element={<YouTubeTagsRankCheckerBlog />}
          />
          <Route
            path="/blog/how-to-export-youtube-search-results"
            element={<YouTubeSearchExportBlog />}
          />
          <Route
            path="/blog/how-to-generate-youtube-tags-for-seo"
            element={<YouTubeTagGeneratorBlog />}
          />
          <Route
            path="/blog/how-to-get-catchy-youtube-video-title-ideas"
            element={<YouTubeTitleGeneratorBlog />}
          />
          <Route
            path="/generate-random-ip-address"
            element={
              <RandomIPGenerator
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/xml-to-json-converter"
            element={
              <XmlToJsonConverter
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/yaml-to-json-converter"
            element={
              <YamlToJsonConverter
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/json-to-yaml-converter"
            element={
              <JsonToYamlConverter
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/email-validation-checker"
            element={
              <EmailValidator
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/disposable-email-checker"
            element={
              <DisposableEmailChecker
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/email-alias-generator"
            element={
              <EmailAliasGenerator
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/youtube/keyword-rank-tracker-daily"
            element={<Navigate to="/youtube/video-rank-checker" replace />}
          />
          <Route
            path="/url-encoder-online"
            element={
              <UrlEncoderTool
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/url-decoder-online"
            element={
              <UrlDecoderTool
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/json-formatter-online"
            element={
              <JsonFormatter
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/ssl-certificate-checker"
            element={
              <SSLCertificateChecker
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/youtube-tag-extractor"
            element={
              <YouTubeTagExtractor
                user={user}
                login={login}
                isLoggedIn={isLoggedIn}
              />
            }
          />
          <Route
            path="/admin/pending-comments"
            element={
              user?.isAdmin ? (
                <AdminPendingComments
                  user={user}
                  login={login}
                  isLoggedIn={isLoggedIn}
                />
              ) : (
                <Navigate to="/404" replace />
              )
            }
          />
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
        <Footer />
      </Router>
    </ThemeProvider>
  );
}

export default App;
